import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const ResourceCategoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    parent_id: null,
    slug: ''
  });
  
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Check if user is admin
  const checkAdminStatus = useCallback(async () => {
    if (user) {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('subscription_status')
        .eq('user_id', user.id)
        .single();
        
      if (!error && data) {
        const admin = data.subscription_status === 'admin';
        setIsAdmin(admin);
        
        if (!admin) {
          // Redirect non-admin users
          navigate('/resources');
        }
      } else {
        navigate('/resources');
      }
    } else {
      navigate('/signin', { state: { from: '/resources/categories' } });
    }
  }, [user, navigate]);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('resource_categories')
        .select('*')
        .order('name', { ascending: true });
        
      if (error) throw error;
      
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);
  
  useEffect(() => {
    if (isAdmin) {
      fetchCategories();
    }
  }, [isAdmin, fetchCategories]);
  
  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };
  
  // Handle name change and auto-generate slug
  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = generateSlug(name);
    
    if (editingCategory) {
      setEditingCategory({
        ...editingCategory,
        name,
        slug
      });
    } else {
      setNewCategory({
        ...newCategory,
        name,
        slug
      });
    }
  };
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (editingCategory) {
      setEditingCategory({
        ...editingCategory,
        [name]: value
      });
    } else {
      setNewCategory({
        ...newCategory,
        [name]: value
      });
    }
  };
  
  // Handle parent category selection
  const handleParentChange = (e) => {
    // Don't parse to integer, keep as string/UUID or null
    const value = e.target.value === '' ? null : e.target.value;
    
    if (editingCategory) {
      setEditingCategory({
        ...editingCategory,
        parent_id: value
      });
    } else {
      setNewCategory({
        ...newCategory,
        parent_id: value
      });
    }
  };
  
  // Start editing a category
  const handleEditClick = (category) => {
    setEditingCategory(category);
    setNewCategory({
      name: '',
      description: '',
      parent_id: null,
      slug: ''
    });
    setError(null);
    setSuccess(null);
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditingCategory(null);
    setError(null);
    setSuccess(null);
  };
  
  // Handle form submission for new or edited category
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError(null);
    setSuccess(null);
    setSaving(true);
    
    try {
      const categoryData = editingCategory || newCategory;
      
      if (!categoryData.name) {
        throw new Error('Category name is required');
      }
      
      if (!categoryData.slug) {
        throw new Error('Category slug is required');
      }
      
      // Check for existing slug (except for the category being edited)
      const existingCategory = categories.find(
        c => c.slug === categoryData.slug && (!editingCategory || c.id !== editingCategory.id)
      );
      
      if (existingCategory) {
        throw new Error('A category with this slug already exists');
      }
      
      if (editingCategory) {
        // Update existing category
        const { error } = await supabase
          .from('resource_categories')
          .update({
            name: categoryData.name,
            description: categoryData.description,
            parent_id: categoryData.parent_id,
            slug: categoryData.slug,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingCategory.id);
          
        if (error) throw error;
        
        setSuccess('Category updated successfully!');
        setEditingCategory(null);
      } else {
        // Create new category - make sure we don't include any unexpected properties
        const categoryToInsert = {
          name: categoryData.name,
          description: categoryData.description,
          parent_id: categoryData.parent_id,
          slug: categoryData.slug,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        // Create new category using a simplified query
        const { error } = await supabase
          .from('resource_categories')
          .insert([categoryToInsert]);
          
        if (error) throw error;
        
        setSuccess('Category created successfully!');
        setNewCategory({
          name: '',
          description: '',
          parent_id: null,
          slug: ''
        });
      }
      
      // Refresh categories
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setError(error.message || 'Failed to save category. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This will also affect resources assigned to this category.')) {
      return;
    }
    
    setError(null);
    setSuccess(null);
    
    try {
      // First, update resources that use this category to set category_id to null
      const { error: resourceUpdateError } = await supabase
        .from('resources')
        .update({ category_id: null })
        .eq('category_id', categoryId);
        
      if (resourceUpdateError) throw resourceUpdateError;
      
      // Then, update categories that use this as parent to set parent_id to null
      const { error: categoryUpdateError } = await supabase
        .from('resource_categories')
        .update({ parent_id: null })
        .eq('parent_id', categoryId);
        
      if (categoryUpdateError) throw categoryUpdateError;
      
      // Finally, delete the category
      const { error: deleteError } = await supabase
        .from('resource_categories')
        .delete()
        .eq('id', categoryId);
        
      if (deleteError) throw deleteError;
      
      setSuccess('Category deleted successfully!');
      
      // Refresh categories
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      setError('Failed to delete category. Please try again later.');
    }
  };
  
  // Build category hierarchy
  const buildCategoryTree = (items, parentId = null) => {
    return items
      .filter(item => item.parent_id === parentId)
      .map(item => ({
        ...item,
        children: buildCategoryTree(items, item.id)
      }));
  };
  
  // Render category options recursively with indentation
  const renderCategoryOptions = (categories, level = 0, excludeId = null) => {
    return categories.map(category => {
      // Skip this category as an option if it's the one being edited
      if (category.id === excludeId) return null;
      
      return (
        <React.Fragment key={category.id}>
          <option value={category.id}>
            {'\u00A0'.repeat(level * 4)}{level > 0 ? '└ ' : ''}{category.name}
          </option>
          {category.children && renderCategoryOptions(category.children, level + 1, excludeId)}
        </React.Fragment>
      );
    });
  };
  
  // Render category list recursively
  const renderCategoryList = (categories, level = 0) => {
    return categories.map(category => (
      <React.Fragment key={category.id}>
        <tr className={level % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {'\u00A0'.repeat(level * 2)}{level > 0 ? '└ ' : ''}{category.name}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {category.slug}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {category.description ? (
              category.description.length > 50 
                ? `${category.description.substring(0, 50)}...` 
                : category.description
            ) : (
              <span className="text-gray-400 italic">No description</span>
            )}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <button
              onClick={() => handleEditClick(category)}
              className="text-primary-600 hover:text-primary-900 mr-4"
            >
              Edit
            </button>
            <button
              onClick={() => handleDeleteCategory(category.id)}
              className="text-red-600 hover:text-red-900"
            >
              Delete
            </button>
          </td>
        </tr>
        {category.children && renderCategoryList(category.children, level + 1)}
      </React.Fragment>
    ));
  };
  
  if (loading) {
    return <LoadingScreen message="Loading categories..." />;
  }
  
  if (!isAdmin) {
    return <LoadingScreen message="Checking permissions..." />;
  }
  
  const categoryTree = buildCategoryTree(categories);
  const categoryForm = editingCategory || newCategory;
  
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Resource Categories</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and organize categories for your resources
          </p>
        </div>
        <Link
          to="/resources/manage"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Back to Resources
        </Link>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-600">{success}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Category Form */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="px-6 py-4">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={categoryForm.name}
                    onChange={handleNameChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                    Slug <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={categoryForm.slug}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    URL-friendly identifier. Auto-generated from name, but can be edited.
                  </p>
                </div>
                
                <div>
                  <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700">
                    Parent Category
                  </label>
                  <select
                    id="parent_id"
                    name="parent_id"
                    value={categoryForm.parent_id || ''}
                    onChange={handleParentChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="">None (Top Level)</option>
                    {renderCategoryOptions(categoryTree, 0, editingCategory?.id)}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows="3"
                    value={categoryForm.description || ''}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  {editingCategory && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={saving}
                    className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                      saving ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {saving ? 'Saving...' : editingCategory ? 'Update Category' : 'Add Category'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        
        {/* Category List */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Categories</h2>
            </div>
            
            <div className="overflow-x-auto">
              {categories.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <p className="text-gray-500">No categories found. Create your first category.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Slug
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {renderCategoryList(categoryTree)}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCategoryPage; 